import {
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
  useMediaQuery
} from '@material-ui/core'
import clsx from 'clsx'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { Skeleton } from '@material-ui/lab'
import LoadingCard from 'src/components/LoadingCard'
import { basicColors } from 'src/theme'
import { returnDuration } from '../pv/utils'

import {
  Info as InfoIcon,
  Link as LinkIcon
} from 'react-feather'
import { anomaliesLimit } from 'src/slices/pv/plantView'
import { alog } from 'src/utils/apioLog'

// stile CSS generale
const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    width: '100%'
  },
  table: {
    minWidth: 620,
    width: '100%'
  },
  tableHeadCell: {
    cursor: 'pointer',
    fontSize: '0.78rem',
    fontWeight: 600,
    padding: '8px 8px'
  },
  anomalyCell: {
    cursor: 'pointer',
    fontSize: '0.78rem',
    padding: '8px 8px',
    wordBreak: 'break-word'
  },
  chipOpen: {
    backgroundColor: basicColors.darkRed,
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.75rem',
    minWidth: 70,
    cursor: 'pointer'
  },
  chipClosed: {
    backgroundColor: basicColors.darkGreen,
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.75rem',
    minWidth: 70,
    cursor: 'pointer'
  },
  cicle: {
    color: theme.palette.primary.main
  },
  popoverContainer: {
    width: 360,
    backgroundColor: basicColors.background,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1)
  },
  paper: {
    maxWidth: 700,
    padding: theme.spacing(2)
  }
}))

// funzione che ordina le anomalie mostrando sempre prima quelle aperte e poi quelle chiuse
export const sortAnomalies = (list) => {
  if (!list || !Array.isArray(list) || list.length === 0 || list[0] === '-') {
    return list
  }
  return [...list].sort((a, b) => {
    const aOpen = !a.endedAt || a.endedAt === ''
    const bOpen = !b.endedAt || b.endedAt === ''

    // 1. Sempre prima tutte le anomalie aperte, poi quelle chiuse
    if (aOpen && !bOpen) return -1
    if (!aOpen && bOpen) return 1

    // 2. Tra anomalie dello stesso stato: priorità alla severity più alta
    const aSeverity = Number(a.severity?.[(a.severity?.length ?? 0) - 1]?.level || 0)
    const bSeverity = Number(b.severity?.[(b.severity?.length ?? 0) - 1]?.level || 0)
    if (!isNaN(aSeverity) && !isNaN(bSeverity) && bSeverity !== aSeverity) {
      return bSeverity - aSeverity
    }

    // 3. A parità di severity: ordiniamo per startedAt o endedAt decrescente
    if (aOpen) {
      const aStart = a.startedAt ? new Date(a.startedAt).getTime() : 0
      const bStart = b.startedAt ? new Date(b.startedAt).getTime() : 0
      return bStart - aStart
    } else {
      const aEnd = a.endedAt ? new Date(a.endedAt).getTime() : 0
      const bEnd = b.endedAt ? new Date(b.endedAt).getTime() : 0
      return bEnd - aEnd
    }
  })
}

const basicFilters = {
  plant: [],
  identifier: [],
  name: [],
  message: [],
  severity: [
    { label: '1', value: 1, selected: false },
    { label: '2', value: 2, selected: false },
    { label: '3', value: 3, selected: false },
    { label: '4', value: 4, selected: false },
    { label: '5', value: 5, selected: false }
  ],
  durationOperator: '>',
  durationChoice: '',
  duration: [],
  externalUrl: ''
}

// Funzione per ricavare la descrizione dell'anomalia
const getAnomalyDescription = (row) => {
  if (!row) return '-'
  if (row.description && row.description !== 'N.F.') {
    return row.description
  }
  if (row.configuration?.description && row.configuration.description !== 'N.F.') {
    return row.configuration.description
  }
  if (row.metadata?.message) {
    return row.metadata.message
  }
  if (row.name && row.name !== 'N.F.') {
    return row.name
  }
  return '-'
}

// Helper che verifica se una stringa è un UUID o un URI del tipo /devices/uuid o /orientations/uuid
export const isUUIDOrUri = (str) => {
  if (typeof str !== 'string') return false
  const clean = str.trim()
  if (clean.includes('/')) return true
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(clean)
}

// Helper per cercare un dispositivo o un orientamento per ID/UUID/nome o oggetto
export const findDeviceOrOrientation = (idOrObj, allDevices = []) => {
  if (!idOrObj) return null
  if (typeof idOrObj === 'object') {
    if (idOrObj.name && !isUUIDOrUri(idOrObj.name)) return idOrObj.name
    if (idOrObj.label && !isUUIDOrUri(idOrObj.label)) return idOrObj.label
    if (idOrObj.title && !isUUIDOrUri(idOrObj.title)) return idOrObj.title
    if (idOrObj.orientationName && !isUUIDOrUri(idOrObj.orientationName)) return idOrObj.orientationName
    if (idOrObj.data?.name && !isUUIDOrUri(idOrObj.data.name)) return idOrObj.data.name
    if (idOrObj.metadata?.name && !isUUIDOrUri(idOrObj.metadata.name)) return idOrObj.metadata.name
    const candidateId = idOrObj.uuid || idOrObj.id || idOrObj._id || idOrObj.orientation
    if (candidateId) {
      return findDeviceOrOrientation(candidateId, allDevices)
    }
    return null
  }

  if (typeof idOrObj !== 'string') return null

  let cleanId = idOrObj.trim()
  if (cleanId.startsWith('/plants')) {
    return 'Impianto'
  }
  const isOrientationUri = cleanId.includes('/orientations')
  if (cleanId.includes('/')) {
    cleanId = cleanId.slice(cleanId.lastIndexOf('/') + 1).split('?')[0].split('#')[0].trim()
  }

  const found = allDevices.find((d) => {
    if (!d) return false
    const dUuid = d.uuid ? String(d.uuid).trim() : null
    const dId = d.id ? String(d.id).trim() : null
    const dUnderscoreId = d._id ? String(d._id).trim() : null
    const dName = d.name ? String(d.name).trim() : null

    return (
      dUuid === cleanId ||
      dId === cleanId ||
      dUnderscoreId === cleanId ||
      (dUuid && dUuid.toLowerCase() === cleanId.toLowerCase()) ||
      dName === cleanId
    )
  })

  if (found) {
    return (
      found.name ||
      found.label ||
      found.title ||
      found.orientationName ||
      found.data?.name ||
      found.metadata?.name ||
      (found.azimuth !== undefined ? `Orientamento (Azimuth ${found.azimuth}°, Tilt ${found.tilt}°)` : isOrientationUri ? 'Orientamento' : null)
    )
  }

  return null
}

// Funzione per ricavare il nome del dispositivo/risorsa/orientamento coinvolto
export const getResourceName = (anomaly, allDevices = []) => {
  if (!anomaly) return '-'

  // 1. Campi diretti nome risorsa / dispositivo / orientamento arricchiti dal backend
  const directCandidates = [
    anomaly.resourceName,
    anomaly.deviceName,
    anomaly.orientationName,
    anomaly.resource,
    anomaly.device,
    anomaly.metadata?.resourceName,
    anomaly.metadata?.deviceName,
    anomaly.metadata?.orientationName,
    anomaly.metadata?.resource,
    anomaly.metadata?.device
  ]

  for (const c of directCandidates) {
    if (!c) continue
    if (typeof c === 'string' && !isUUIDOrUri(c)) return c
    if (typeof c === 'object') {
      const name = c.name || c.label || c.title || c.orientationName || c.deviceName
      if (name && typeof name === 'string' && !isUUIDOrUri(name)) return name
    }
  }

  // 2. Controllo orientamento diretto (oggetto o UUID)
  if (anomaly.orientation) {
    const resolved = findDeviceOrOrientation(anomaly.orientation, allDevices)
    if (resolved) return resolved
    if (typeof anomaly.orientation === 'string' && !anomaly.orientation.includes('/') && isNaN(Number(anomaly.orientation)) && anomaly.orientation.length < 30 && !isUUIDOrUri(anomaly.orientation)) {
      return anomaly.orientation
    }
    if (typeof anomaly.orientation === 'string' && (anomaly.orientation.includes('orientations') || isUUIDOrUri(anomaly.orientation))) {
      return 'Orientamento'
    }
  }
  if (anomaly.metadata?.orientation) {
    const resolved = findDeviceOrOrientation(anomaly.metadata.orientation, allDevices)
    if (resolved) return resolved
    if (typeof anomaly.metadata.orientation === 'string' && !anomaly.metadata.orientation.includes('/') && isNaN(Number(anomaly.metadata.orientation)) && anomaly.metadata.orientation.length < 30 && !isUUIDOrUri(anomaly.metadata.orientation)) {
      return anomaly.metadata.orientation
    }
    if (typeof anomaly.metadata.orientation === 'string' && (anomaly.metadata.orientation.includes('orientations') || isUUIDOrUri(anomaly.metadata.orientation))) {
      return 'Orientamento'
    }
  }

  // 3. Array devices / resources / orientations già presenti sull'anomalia
  if (Array.isArray(anomaly.devices) && anomaly.devices.length > 0) {
    const names = anomaly.devices.map((d) => findDeviceOrOrientation(d, allDevices) || (typeof d === 'string' && !isUUIDOrUri(d) ? d : d.name)).filter(Boolean)
    if (names.length > 0) return names.join(', ')
  }
  if (Array.isArray(anomaly.resources) && anomaly.resources.length > 0) {
    const names = anomaly.resources.map((d) => findDeviceOrOrientation(d, allDevices) || (typeof d === 'string' && !isUUIDOrUri(d) ? d : d.name)).filter(Boolean)
    if (names.length > 0) return names.join(', ')
  }
  if (Array.isArray(anomaly.orientations) && anomaly.orientations.length > 0) {
    const names = anomaly.orientations.map((d) => findDeviceOrOrientation(d, allDevices) || (typeof d === 'string' && !isUUIDOrUri(d) ? d : d.name)).filter(Boolean)
    if (names.length > 0) return names.join(', ')
    return 'Orientamento'
  }

  // 4. Risoluzione da relatedResources
  if (Array.isArray(anomaly.relatedResources) && anomaly.relatedResources.length > 0) {
    const resolved = anomaly.relatedResources.map((res) => {
      const match = findDeviceOrOrientation(res, allDevices)
      if (match) return match
      if (typeof res === 'object' && res !== null) {
        if (res.name && !isUUIDOrUri(res.name)) return res.name
        if (res.label && !isUUIDOrUri(res.label)) return res.label
        if (res.title && !isUUIDOrUri(res.title)) return res.title
        if (res.resourceType && !isUUIDOrUri(res.resourceType)) return res.resourceType
      }
      if (typeof res === 'string') {
        if (res.includes('/orientations/')) {
          return 'Orientamento'
        }
        if (res.includes('/plants/')) {
          return 'Impianto'
        }
        if (res.includes('/devices/')) {
          return 'Dispositivo'
        }
        if (res.includes('/dataloggers/')) {
          return 'Datalogger'
        }
        const id = res.includes('/') ? res.slice(res.lastIndexOf('/') + 1).split('?')[0].split('#')[0] : res
        return isUUIDOrUri(id) ? 'Dispositivo' : id
      }
      return null
    }).filter(Boolean)

    if (resolved.length > 0) {
      return resolved.join(', ')
    }
  }

  // 5. Fallback su metadata / resourceType
  if (anomaly.metadata?.device && typeof anomaly.metadata.device === 'string') {
    const match = findDeviceOrOrientation(anomaly.metadata.device, allDevices)
    if (match) return match
    if (!isUUIDOrUri(anomaly.metadata.device)) return anomaly.metadata.device
  }
  if (anomaly.metadata?.resource && typeof anomaly.metadata.resource === 'string') {
    const match = findDeviceOrOrientation(anomaly.metadata.resource, allDevices)
    if (match) return match
    if (!isUUIDOrUri(anomaly.metadata.resource)) return anomaly.metadata.resource
  }
  if (anomaly.resourceType && !isUUIDOrUri(anomaly.resourceType)) return anomaly.resourceType

  return '-'
}

// Funzione per ricavare il nome dell'impianto dell'anomalia
export const getPlantName = (anomaly, allPlants = []) => {
  if (!anomaly) return '-'

  // 1. Campi diretti nome impianto arricchiti dal backend
  const directPlantCandidates = [
    anomaly.plantName,
    anomaly.plant,
    anomaly.metadata?.plantName,
    anomaly.metadata?.plant
  ]

  for (const p of directPlantCandidates) {
    if (!p) continue
    if (typeof p === 'string' && !isUUIDOrUri(p)) return p
    if (typeof p === 'object') {
      const name = p.name || p.label || p.title
      if (name && typeof name === 'string' && !isUUIDOrUri(name)) return name
    }
  }

  const plantId = anomaly.plantId || anomaly.plant_id || anomaly.plantUUID ||
    (typeof anomaly.metadata?.plant === 'string' ? anomaly.metadata.plant : null)

  if (plantId && Array.isArray(allPlants)) {
    const match = allPlants.find(p => p && (p.uuid === plantId || p.id === plantId || p._id === plantId))
    if (match?.name) return match.name
  }

  return '-'
}

// Funzione helper per estrarre tutti i dispositivi, datalogger e orientamenti dagli impianti in memoria senza chiamate di rete
export const extractPlantResources = (plants = []) => {
  const devices = []
  const dataloggers = []
  const orientations = []

  if (!Array.isArray(plants)) {
    return { devices, dataloggers, orientations }
  }

  plants.forEach((plant) => {
    if (!plant || typeof plant !== 'object') return
    const pId = plant.uuid || plant.id || plant._id
    const plantName = plant.name

    // 1. Datalogger e dispositivi contenuti
    const plantDls = plant.metadata?.addedDataloggers || plant.addedDataloggers || plant.dataloggers || []
    if (Array.isArray(plantDls)) {
      plantDls.forEach((dl) => {
        if (!dl) return
        const dlId = dl.uuid || dl.id || dl._id
        const dlName = dl.name || dl.label || 'Datalogger'
        dataloggers.push({
          ...dl,
          uuid: dlId,
          plantId: pId,
          plantName,
          name: dlName
        })

        const nestedDevices = dl.includes?.devices || dl.devices || []
        if (Array.isArray(nestedDevices)) {
          nestedDevices.forEach((dev) => {
            if (!dev) return
            const devId = dev.uuid || dev.id || dev._id
            const rawDevName = dev.name || dev.label || dev.deviceName || 'Dispositivo'
            const composedName = dev.metadata?.inverterNum !== null && dev.metadata?.inverterNum !== undefined
              ? `${dlName} - ${rawDevName} (${dev.metadata.inverterNum})`
              : (dlName ? `${dlName} - ${rawDevName}` : rawDevName)

            devices.push({
              ...dev,
              uuid: devId,
              plantId: pId,
              plantName,
              name: composedName
            })
          })
        }
      })
    }

    // 2. Dispositivi diretti dell'impianto
    const plantDevs = plant.metadata?.devices || plant.devices || []
    if (Array.isArray(plantDevs)) {
      plantDevs.forEach((dev) => {
        if (!dev) return
        const devId = dev.uuid || dev.id || dev._id
        if (devId && devices.some((d) => (d.uuid || d.id || d._id) === devId)) return
        devices.push({
          ...dev,
          uuid: devId,
          plantId: pId,
          plantName,
          name: dev.name || dev.label || dev.deviceName || 'Dispositivo'
        })
      })
    }

    // 3. Orientamenti dell'impianto
    const plantOrts = plant.metadata?.orientations || plant.orientations || []
    if (Array.isArray(plantOrts)) {
      plantOrts.forEach((ort, idx) => {
        if (!ort) return
        const ortId = ort.uuid || ort.id || ort._id
        orientations.push({
          ...ort,
          uuid: ortId,
          plantId: pId,
          plantName,
          name: ort.name || ort.label || ort.title || ort.orientationName || `Orientamento ${idx + 1}`
        })
      })
    }
  })

  return { devices, dataloggers, orientations }
}

// Funzione per normalizzare un'anomalia per la vista d'insieme e per la sidenav
export const normalizeOverviewAnomaly = (anomaly, categories = [], allDevices = [], allPlants = []) => {
  if (!anomaly) return {}

  const plantName = getPlantName(anomaly, allPlants)
  const resourceName = getResourceName(anomaly, allDevices)

  let name = 'N.F.'
  if (anomaly.name && anomaly.name !== 'N.F.') {
    name = anomaly.name
  } else if (anomaly.metadata?.message) {
    name = anomaly.metadata.message
  } else if (anomaly.configuration?.name) {
    name = anomaly.configuration.name
  }

  let description = '-'
  if (anomaly.description && anomaly.description !== 'N.F.' && anomaly.description !== '-') {
    description = anomaly.description
  } else if (anomaly.configuration?.description) {
    description = anomaly.configuration.description
  } else if (anomaly.metadata?.message && anomaly.metadata.message !== name) {
    description = anomaly.metadata.message
  } else if (anomaly.configuration?.name && anomaly.configuration.name !== name) {
    description = anomaly.configuration.name
  }

  const thisConfig = anomaly.configuration || {}
  const rawCat = thisConfig.category || (typeof anomaly.category === 'string' ? anomaly.category : anomaly.category?.label) || ''
  const rawSub = thisConfig.subCategory || (typeof anomaly.subCategory === 'string' ? anomaly.subCategory : anomaly.subCategory?.label) || ''

  const matchedCat = Array.isArray(categories) ? categories.find(c => {
    if (!c) return false
    const cName = (c.name || '').toLowerCase()
    const cLabel = (c.label || '').toLowerCase()
    const target = rawCat.toLowerCase()
    return cName === target || cLabel === target || (target && (target.includes(cName) || cName.includes(target)))
  }) : null

  const categoryLabel = matchedCat?.label || matchedCat?.name || (typeof anomaly.category === 'string' ? anomaly.category : '') || rawCat

  const matchedSub = matchedCat && Array.isArray(matchedCat.subCategory) ? matchedCat.subCategory.find(s => {
    if (!s) return false
    const sName = (s.name || '').toLowerCase()
    const sLabel = (s.label || '').toLowerCase()
    const target = rawSub.toLowerCase()
    return sName === target || sLabel === target || (target && (target.includes(sName) || sName.includes(target)))
  }) : null

  const subCategoryObj = {
    label: matchedSub?.label || matchedSub?.name || (typeof anomaly.subCategory === 'object' ? anomaly.subCategory?.label : rawSub) || '',
    color: matchedSub?.color || (typeof anomaly.subCategory === 'object' ? anomaly.subCategory?.color : '') || basicColors.primary
  }

  let duration = anomaly.duration
  if (duration === undefined || duration === null) {
    const start = anomaly.startedAt ? new Date(anomaly.startedAt).getTime() : 0
    const end = anomaly.endedAt ? new Date(anomaly.endedAt).getTime() : Date.now()
    if (start > 0) {
      duration = Math.max(0, end - start)
    }
  }

  return {
    ...anomaly,
    plantName: plantName !== '-' ? plantName : (anomaly.plantName || '-'),
    resourceName: resourceName !== '-' ? resourceName : (anomaly.resourceName || '-'),
    name,
    description,
    category: categoryLabel,
    subCategory: subCategoryObj,
    duration,
    selected: anomaly.selected !== undefined ? anomaly.selected : true,
    notes: anomaly.notes || '',
    externalUrl: anomaly.externalUrl || ''
  }
}

// Funzione per troncare il testo in base ai caratteri massimi del viewport e mostrare Tooltip con testo completo
const renderTruncatedText = (text, maxChars) => {
  if (!text || text === '-') {
    return '-'
  }
  const str = String(text).replace(/[\r\n]+/g, ' ')
  const isTruncated = str.length > maxChars
  const displayText = isTruncated ? `${str.slice(0, maxChars).trim()}...` : str

  if (isTruncated) {
    return (
      <Tooltip title={str} placement='top-start' arrow interactive>
        <span style={{ display: 'inline-block', maxWidth: '100%', wordBreak: 'break-word' }}>{displayText}</span>
      </Tooltip>
    )
  }

  return displayText
}

// componente principale
const AnomaliesListView = ({
  className,
  isLoading = false,
  anomaliesList,
  getMoreAnomalies,
  totalAnomalies,
  limit,
  onRowClick,
  onSelectedChanged,
  onFilterChange,
  periodHasChanged,
  devices: propDevices,
  dataloggers: propDataloggers,
  orientations: propOrientations,
  plants: propPlants,
  showPlantColumn = false,
  ...rest
}) => {
  const classes = useStyles()
  const storePvDevices = useSelector((state) => state.pvPlantView?.devices || state.pvPlant?.devices)
  const storeLedDevices = useSelector((state) => state.ledPlantView?.devices || state.ledPlant?.devices)
  const storeIndDevices = useSelector((state) => state.industryConsumptionPlantView?.devices || state.industryConsumptionPlant?.devices)
  const storeDevices = storePvDevices || storeLedDevices || storeIndDevices

  const storePvDls = useSelector((state) => state.pvPlantView?.dataloggers || state.pvPlant?.dataloggers)
  const storeLedDls = useSelector((state) => state.ledPlantView?.dataloggers || state.ledPlant?.dataloggers)
  const storeIndDls = useSelector((state) => state.industryConsumptionPlantView?.dataloggers || state.industryConsumptionPlant?.dataloggers)
  const storeDataloggers = storePvDls || storeLedDls || storeIndDls

  const storePvOrts = useSelector((state) => state.pvPlantView?.orientations || state.pvPlant?.orientations)
  const storeLedOrts = useSelector((state) => state.ledPlantView?.orientations || state.ledPlant?.orientations)
  const storeIndOrts = useSelector((state) => state.industryConsumptionPlantView?.orientations || state.industryConsumptionPlant?.orientations)
  const storeOrientations = storePvOrts || storeLedOrts || storeIndOrts

  const storePlants = useSelector((state) => state.plants?.plants)

  const allPlants = useMemo(() => {
    if (Array.isArray(propPlants) && propPlants.length > 0) {
      return propPlants
    }
    if (Array.isArray(storePlants) && storePlants[0] !== '-') {
      return storePlants
    }
    return []
  }, [propPlants, storePlants])

  const plantDevices = useMemo(() => {
    const devs = Array.isArray(propDevices) && propDevices.length > 0
      ? propDevices
      : (Array.isArray(storeDevices) && storeDevices[0] !== '-' ? storeDevices : [])
    const dls = Array.isArray(propDataloggers) && propDataloggers.length > 0
      ? propDataloggers
      : (Array.isArray(storeDataloggers) && storeDataloggers[0] !== '-' ? storeDataloggers : [])
    const orts = Array.isArray(propOrientations) && propOrientations.length > 0
      ? propOrientations
      : (Array.isArray(storeOrientations) && storeOrientations[0] !== '-' ? storeOrientations : [])

    const nestedOrientations = []
    devs.forEach((dev) => {
      if (dev && dev.orientation && typeof dev.orientation === 'object' && (dev.orientation.uuid || dev.orientation.name)) {
        nestedOrientations.push(dev.orientation)
      }
    })

    return [...devs, ...dls, ...orts, ...nestedOrientations]
  }, [propDevices, storeDevices, propDataloggers, storeDataloggers, propOrientations, storeOrientations])

  const isDownSm = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const isDownMd = useMediaQuery((theme) => theme.breakpoints.down('md'))
  const isDownLg = useMediaQuery((theme) => theme.breakpoints.down('lg'))
  const isUpXl = useMediaQuery((theme) => theme.breakpoints.up('xl'))

  const maxDescriptionChars = isDownSm ? 28 : isDownMd ? 40 : isUpXl ? 45 : 70
  const maxResourceChars = isDownSm ? 16 : isDownMd ? 20 : isUpXl ? 25 : 35
  const maxPlantChars = isDownSm ? 14 : isDownMd ? 18 : isUpXl ? 20 : 28

  const [skip, setSkip] = useState(0)
  const [endPaginationReached, setEndPaginationReached] = useState(false)
  const [isGettingPagination, setIsGettingPagination] = useState(false)
  const [anomaliesRows, setAnomaliesRows] = useState(sortAnomalies(anomaliesList))
  const [filters, setFilters] = useState(basicFilters)
  const [filtersToSend, setFiltersToSend] = useState({})
  const [anchorEl, setAnchorEl] = useState(null)
  const [openPopper, setOpenPopper] = useState(false)
  const [nothingHasChanged, setNothingHasChanged] = useState(true)

  // funzione che ritorna i filtri
  const returnDefaultFilters = (anomalies, prevFilters) => {
    if (anomalies && anomalies.length > 0 && anomalies[0] !== '-') {
      const newFilters = JSON.parse(JSON.stringify(basicFilters))
      anomalies.forEach(anomaly => {
        // se mostriamo la colonna impianto, raccogliamo gli impianti
        if (showPlantColumn) {
          const pName = getPlantName(anomaly, allPlants)
          const pId = anomaly.plantId || anomaly.plant_id || anomaly.uuid
          if (pName && pName !== '-') {
            const pIndex = newFilters.plant.findIndex(p => p.label === pName)
            if (pIndex === -1) {
              newFilters.plant.push({
                label: pName,
                value: pId || pName,
                selected: false
              })
            }
          }
        }
        // controllo se ho già questa categoria sennò la pusho
        if (anomaly.configuration) {
          const catIndex = newFilters.identifier.findIndex(cat => {
            return cat.label === `${anomaly.configuration.subCategory} (${anomaly.configuration.category})`
          })
          if (catIndex === -1) {
            newFilters.identifier.push({
              label: `${anomaly.configuration.subCategory} (${anomaly.configuration.category})`,
              value: [anomaly.configuration.identifier],
              selected: false
            })
          } else {
            const identifierIndex = newFilters.identifier[catIndex].value.indexOf(anomaly.configuration.identifier)
            if (identifierIndex === -1) {
              newFilters.identifier[catIndex].value.push(anomaly.configuration.identifier)
            }
          }
        }
        // controllo se ho già il name
        if (anomaly.configuration) {
          const nameIndex = newFilters.name.findIndex(name => name.value === anomaly.configuration.name)
          if (nameIndex === -1) {
            newFilters.name.push({
              label: anomaly.configuration.name,
              value: anomaly.configuration.name,
              selected: false
            })
          }
        }
        // controllo se c'è il message
        if (anomaly.metadata && anomaly.metadata.message) {
          const messageIndex = newFilters.message.findIndex(message => message.value === anomaly.metadata.message)
          if (messageIndex === -1) {
            newFilters.message.push({
              label: anomaly.metadata.message,
              value: anomaly.metadata.message,
              selected: false
            })
          }
        }
      })
      // mi prendo la duration maggiore
      const maxDuration = anomalies.reduce((acc, anomaly) => {
        if (anomaly.duration > acc) {
          return anomaly.duration
        }
        return acc
      }, 0)
      if (maxDuration > 0) {
        const slice = maxDuration / 4
        newFilters.durationOperator = '<'
        newFilters.duration = [
          { label: slice - 60000, value: slice - 60000, selected: false },
          { label: slice * 2 - 60000, value: slice * 2 - 60000, selected: false },
          { label: slice * 3 - 60000, value: slice * 3 - 60000, selected: false },
          { label: slice * 4 - 60000, value: slice * 4 - 60000, selected: false }
        ]
      } else {
        newFilters.durationOperator = '>'
        newFilters.duration = [
          { label: 840000, value: 840000, selected: false },
          { label: 840000 * 2, value: 840000 * 2, selected: false },
          { label: 840000 * 3, value: 840000 * 3, selected: false },
          { label: 840000 * 4, value: 840000 * 4, selected: false }
        ]
      }

      if (prevFilters) {
        if (prevFilters.plant && prevFilters.plant.length > 0) {
          newFilters.plant.forEach((newObj) => {
            const thisIndex = prevFilters.plant.findIndex(p => p.label === newObj.label)
            if (thisIndex > -1) {
              newObj.selected = prevFilters.plant[thisIndex].selected
            }
          })
        }
        if (prevFilters.identifier && prevFilters.identifier.length > 0) {
          newFilters.identifier.forEach((newObj) => {
            const thisIndex = prevFilters.identifier.findIndex(cat => cat.value === newObj.value)
            if (thisIndex > -1) {
              newObj.selected = prevFilters.identifier[thisIndex].selected
            }
          })
        }
        if (prevFilters.name && prevFilters.name.length > 0) {
          newFilters.name.forEach((newObj) => {
            const thisIndex = prevFilters.name.findIndex(name => name.value === newObj.value)
            if (thisIndex > -1) {
              newObj.selected = prevFilters.name[thisIndex].selected
            }
          })
        }
        if (prevFilters.message && prevFilters.message.length > 0) {
          newFilters.message.forEach((newObj) => {
            const thisIndex = prevFilters.message.findIndex(message => message.value === newObj.value)
            if (thisIndex > -1) {
              newObj.selected = prevFilters.message[thisIndex].selected
            }
          })
        }
        if (prevFilters.severity && prevFilters.severity.length > 0) {
          newFilters.severity.forEach((newObj) => {
            const thisIndex = prevFilters.severity.findIndex(severity => severity.value === newObj.value)
            if (thisIndex > -1) {
              newObj.selected = prevFilters.severity[thisIndex].selected
            }
          })
        }
        newFilters.durationChoice = prevFilters.durationChoice
        if (prevFilters.durationChoice) {
          newFilters.duration[prevFilters.durationChoice].selected = true
        }
        newFilters.durationOperator = prevFilters.durationOperator
        newFilters.externalUrl = prevFilters.externalUrl
      }

      alog('newFilters => ', newFilters, 'anomalie')
      return newFilters
    }
  }

  // funzione che apre il popper con la sezione dei filtri
  const openFilterPopper = (event) => {
    setAnchorEl(event.currentTarget)
    setOpenPopper((prev) => !prev)
  }

  // funzione che chiude popper se clicchi fuori
  const handleClosePopover = () => {
    setOpenPopper(false)
  }

  // ogni update aggiorno la variabile delle anomalie
  useEffect(() => {
    setAnomaliesRows(sortAnomalies(anomaliesList))
    if (periodHasChanged) {
      setFilters(returnDefaultFilters(anomaliesList))
    }
    setIsGettingPagination(false)
    if (anomaliesList.length < (anomaliesLimit + 1)) {
      setSkip(0)
    }
  }, [anomaliesList, periodHasChanged])

  // funzione che viene lanciata al click su una riga
  const handleClick = (event, row) => {
    if (onRowClick) {
      onRowClick(row)
    }
  }

  // funzione che setta il select dei checkbox
  const handleCheckboxChange = (section, index) => {
    const newArray = [...filters[section]]
    newArray[index].selected = !newArray[index].selected
    setFilters({
      ...filters,
      [section]: newArray
    })
    setNothingHasChanged(false)
  }

  // funzione che setta la scelta dei radio button
  const handleRadioChange = (event) => {
    let newChoice = event.target.value
    if (newChoice === filters.durationChoice) {
      newChoice = ''
    }
    setFilters({
      ...filters,
      durationChoice: newChoice
    })
    setNothingHasChanged(false)
  }

  // funzione che viene lanciata al click su applica
  const handleApplyFilters = () => {
    const filtersToSend = {}
    if (filters.plant && filters.plant.some(p => p.selected === true)) {
      filtersToSend.plant = []
      filters.plant.forEach(p => {
        if (p.selected === true) {
          filtersToSend.plant.push(p.value)
        }
      })
    }
    if (filters.identifier.some(cat => cat.selected === true)) {
      filtersToSend.identifier = []
      filters.identifier.forEach(cat => {
        if (cat.selected === true) {
          filtersToSend.identifier.push(...cat.value)
        }
      })
    }
    if (filters.name.some(name => name.selected === true)) {
      filtersToSend.name = []
      filters.name.forEach(name => {
        if (name.selected === true) {
          filtersToSend.name.push(name.value)
        }
      })
    }
    if (filters.message.some(message => message.selected === true)) {
      filtersToSend.message = []
      filters.message.forEach(message => {
        if (message.selected === true) {
          filtersToSend.message.push(message.value)
        }
      })
    }
    if (filters.severity.some(cat => cat.selected === true)) {
      filtersToSend.severity = []
      filters.severity.forEach(severity => {
        if (severity.selected === true) {
          filtersToSend.severity.push(severity.value)
        }
      })
    }
    if (filters.durationChoice !== '') {
      filtersToSend.duration = {
        operator: filters.durationOperator,
        value: Number(filters.duration[filters.durationChoice].value)
      }
    }
    if (filters.externalUrl !== '') {
      filtersToSend.externalUrl = filters.externalUrl
    }
    if (onFilterChange) {
      onFilterChange(filtersToSend)
      setFiltersToSend(filtersToSend)
      handleClosePopover()
    }
  }

  // funzione che viene lanciata al click su reset
  const handleResetFilters = () => {
    const newFilters = JSON.parse(JSON.stringify(filters))
    Object.keys(newFilters).forEach(key => {
      if (key === 'externalUrl') {
        newFilters[key] = ''
      } else if (key === 'durationOperator') {
        newFilters[key] = '<'
      } else if (key === 'durationChoice') {
        newFilters[key] = ''
      } else {
        newFilters[key].forEach(filter => { filter.selected = false })
      }
    })
    setFilters(newFilters)
    setNothingHasChanged(false)
  }

  return (
    <Card className={clsx(classes.root, className)} {...rest}>
      <Popover
        open={openPopper}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        classes={{
          paper: classes.paper
        }}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center'
        }}
        TransitionProps={{
          onEnter: () => {
            setNothingHasChanged(true)
          }
        }}
      >
        {onFilterChange && filters && ((filters.identifier && filters.identifier.length > 0) || (filters.plant && filters.plant.length > 0))
          ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant='body1'>Seleziona uno o più filtri e clicca su &quot;Applica&quot;.</Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                {showPlantColumn && filters.plant && filters.plant.length > 0 && (
                  <FormControl fullWidth component='fieldset'>
                    <FormLabel component='legend'>Impianto</FormLabel>
                    <FormGroup>
                      {filters.plant.map((plant, index) => {
                        return (
                          <FormControlLabel
                            key={index}
                            control={<Checkbox color='primary' checked={plant.selected} onChange={() => handleCheckboxChange('plant', index)} name={plant.label} />}
                            label={<Tooltip title={plant.label}><Typography style={{ maxWidth: 300 }} noWrap>{plant.label}</Typography></Tooltip>}
                          />
                        )
                      })}
                    </FormGroup>
                  </FormControl>
                )}
                {filters.identifier && filters.identifier.length > 0 && (
                  <FormControl component='fieldset' style={{ marginTop: showPlantColumn ? 8 : 0 }}>
                    <FormLabel component='legend'>Categoria</FormLabel>
                    <FormGroup>
                      {filters.identifier.map((category, index) => {
                        return (
                          <FormControlLabel
                            key={index}
                            control={<Checkbox color='primary' checked={category.selected} onChange={() => handleCheckboxChange('identifier', index)} name={category.label} />}
                            label={category.label}
                          />
                        )
                      })}
                    </FormGroup>
                  </FormControl>
                )}
                <FormControl fullWidth component='fieldset' style={{ marginTop: 8 }}>
                  <FormLabel component='legend'>Descrizione</FormLabel>
                  <FormGroup>
                    {filters.name.map((name, index) => {
                      return (
                        <FormControlLabel
                          key={index}
                          control={<Checkbox color='primary' checked={name.selected} onChange={() => handleCheckboxChange('name', index)} name={name.label} />}
                          label={<Tooltip title={name.label}><Typography style={{ maxWidth: 300 }} noWrap>{name.label}</Typography></Tooltip>}
                        />
                      )
                    })}
                    {filters.message.map((message, index) => {
                      return (
                        <FormControlLabel
                          key={index}
                          control={<Checkbox color='primary' checked={message.selected} onChange={() => handleCheckboxChange('message', index)} name={message.label} />}
                          label={<Tooltip title={message.label}><Typography style={{ maxWidth: 300 }} noWrap>{message.label}</Typography></Tooltip>}
                        />
                      )
                    })}
                  </FormGroup>
                </FormControl>
                <FormControl component='fieldset' style={{ width: '90%', marginTop: 8 }}>
                  <FormLabel component='legend'>Presenza link</FormLabel>
                  <TextField
                    style={{ marginTop: 8 }}
                    id='externalUrl'
                    size='small'
                    select
                    name='externalUrl'
                    placeholder='Scelta...'
                    value={filters.externalUrl}
                    onChange={(event) => {
                      alog('new url choice =>', event.target.value, 'anomalie')
                      setFilters({
                        ...filters,
                        externalUrl: event.target.value
                      })
                      setNothingHasChanged(false)
                    }}
                  >
                    <MenuItem value=''><em>Nessuna scelta</em></MenuItem>
                    <MenuItem value>Con url esterno</MenuItem>
                    <MenuItem value={false}>Senza url esterno</MenuItem>
                  </TextField>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl component='fieldset'>
                  <FormLabel component='legend'>Severity</FormLabel>
                  <FormGroup>
                    {filters.severity.map((severity, index) => {
                      return (
                        <FormControlLabel
                          key={index}
                          control={<Checkbox color='primary' checked={severity.selected} onChange={() => handleCheckboxChange('severity', index)} name={severity.label} />}
                          label={severity.label}
                        />
                      )
                    })}
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl component='fieldset'>
                  <FormLabel component='legend'>Durata anomalia</FormLabel>
                  <RadioGroup
                    aria-label='duration'
                    name='duration'
                    value={filters.durationChoice}
                  >
                    {filters.duration.map((duration, index) => {
                      return (
                        <FormControlLabel key={index} value={String(index)} control={<Radio onClick={handleRadioChange} color='primary' />} label={returnDuration(duration.label)} />
                      )
                    })}
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
                  <Grid item>
                    <Button onClick={handleClosePopover} variant='outlined'>Chiudi</Button>
                    <Button onClick={handleResetFilters} variant='outlined' color='primary' style={{ marginLeft: 16 }}>Resetta</Button>
                  </Grid>
                  <Grid item>
                    <Button onClick={handleApplyFilters} disabled={nothingHasChanged} variant='contained' color='primary'>Applica</Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )
          : (
            <Typography>Non ci sono filtri selezionabili</Typography>
          )}
      </Popover>
      <TableContainer style={{ height: anomaliesRows.length !== 0 && anomaliesRows[0] !== '-' ? '100%' : null, maxHeight: '100%' }}>
        <PerfectScrollbar
          onScrollY={(container) => {
            const heightCondition = container.clientHeight + container.scrollTop - container.scrollHeight
            if (Math.abs(heightCondition) < 1 && !isGettingPagination) {
              if (anomaliesRows.length < totalAnomalies) {
                if (getMoreAnomalies) {
                  setIsGettingPagination(true)
                  getMoreAnomalies(skip + limit)
                  setSkip(skip + limit)
                }
              } else {
                setEndPaginationReached(true)
                setTimeout(() => {
                  setEndPaginationReached(false)
                }, 4000)
              }
            }
          }}
          containerRef={ref => {
            if (ref) {
              ref._getBoundingClientRect = ref.getBoundingClientRect

              ref.getBoundingClientRect = () => {
                const original = ref._getBoundingClientRect()

                return {
                  bottom: original.bottom,
                  left: original.left,
                  right: original.right,
                  top: original.top,
                  width: Math.round(original.width),
                  _width: original.width,
                  height: Math.round(original.height),
                  _height: original.height,
                  x: original.x,
                  y: original.y
                }
              }
            }
          }}
        >
          <Table stickyHeader className={classes.table} aria-labelledby='anomaliesTable' aria-label='Tabella Anomalie'>
            <TableHead>
              <TableRow>
                <TableCell
                  className={classes.tableHeadCell}
                  style={{ minWidth: 80, borderLeft: `5px solid ${basicColors.primary}` }}
                  onClick={openFilterPopper}
                  align='center'
                >
                  Stato
                </TableCell>
                {showPlantColumn && (
                  <TableCell className={classes.tableHeadCell} style={{ minWidth: 105 }} onClick={openFilterPopper}>
                    {filtersToSend.plant && filtersToSend.plant.length > 0
                      ? (
                        <Badge color='primary' badgeContent={filtersToSend.plant.length}>
                          Impianto
                        </Badge>
                      )
                      : 'Impianto'}
                  </TableCell>
                )}
                <TableCell className={classes.tableHeadCell} style={{ minWidth: 100 }} onClick={openFilterPopper}>
                  Risorsa
                </TableCell>
                <TableCell className={classes.tableHeadCell} style={{ minWidth: 140 }} onClick={openFilterPopper}>
                  {(filtersToSend.name && filtersToSend.name.length > 0) || (filtersToSend.message && filtersToSend.message.length > 0)
                    ? (
                      <Badge color='primary' badgeContent={(filtersToSend.name ? filtersToSend.name.length : 0) + (filtersToSend.message ? filtersToSend.message.length : 0)}>
                        Descrizione
                      </Badge>
                    )
                    : 'Descrizione'}
                </TableCell>
                <TableCell className={classes.tableHeadCell} style={{ minWidth: 70 }} onClick={openFilterPopper} align='center'>
                  {filtersToSend.severity && filtersToSend.severity.length > 0
                    ? (
                      <Badge color='primary' badgeContent={filtersToSend.severity.length}>
                        Severity
                      </Badge>
                    )
                    : 'Severity'}
                </TableCell>
                <TableCell className={classes.tableHeadCell} style={{ minWidth: 125 }} onClick={openFilterPopper}>
                  Periodo
                </TableCell>
                <TableCell className={classes.tableHeadCell} style={{ minWidth: 80 }} onClick={openFilterPopper} align='center'>
                  {filtersToSend.duration && filtersToSend.duration.value
                    ? (
                      <Badge color='primary' badgeContent={1}>
                        <Tooltip title={'Durata totale dell\'anomalia'} aria-label='Durata totale' interactive>
                          <Box display='flex' alignItems='center' justifyContent='center'>
                            <span style={{ marginRight: 6 }}>Durata</span>
                            <SvgIcon fontSize='small'>
                              <InfoIcon />
                            </SvgIcon>
                          </Box>
                        </Tooltip>
                      </Badge>
                    )
                    : (
                      <Tooltip title={'Durata totale dell\'anomalia'} aria-label='Durata totale' interactive>
                        <Box display='flex' alignItems='center' justifyContent='center'>
                          <span style={{ marginRight: 6 }}>Durata</span>
                          <SvgIcon fontSize='small'>
                            <InfoIcon />
                          </SvgIcon>
                        </Box>
                      </Tooltip>
                    )}
                </TableCell>
                <TableCell className={classes.tableHeadCell} style={{ minWidth: 48 }} onClick={openFilterPopper} align='center'>
                  {filtersToSend.externalUrl && filtersToSend.externalUrl === true
                    ? (
                      <Badge color='primary' badgeContent={1}>
                        Link
                      </Badge>
                    )
                    : 'Link'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || anomaliesRows[0] === '-'
                ? [...Array(6)].map((_, index) => (
                  <TableRow key={index} style={{ height: 49 }}>
                    <TableCell align='center' className={classes.anomalyCell}>
                      <Skeleton animation='wave' variant='rect' width={52} height={24} style={{ borderRadius: 12, margin: 'auto' }} />
                    </TableCell>
                    {showPlantColumn && (
                      <TableCell className={classes.anomalyCell}>
                        <Skeleton animation='wave' height={20} width='80%' />
                      </TableCell>
                    )}
                    <TableCell className={classes.anomalyCell}>
                      <Skeleton animation='wave' height={20} width='70%' />
                    </TableCell>
                    <TableCell className={classes.anomalyCell}>
                      <Skeleton animation='wave' height={20} width='85%' />
                    </TableCell>
                    <TableCell align='center' className={classes.anomalyCell}>
                      <Skeleton animation='wave' height={20} width={24} style={{ margin: 'auto' }} />
                    </TableCell>
                    <TableCell className={classes.anomalyCell}>
                      <Skeleton animation='wave' height={20} width='75%' />
                    </TableCell>
                    <TableCell align='center' className={classes.anomalyCell}>
                      <Skeleton animation='wave' height={20} width={45} style={{ margin: 'auto' }} />
                    </TableCell>
                    <TableCell align='center' className={classes.anomalyCell}>
                      <Skeleton animation='wave' variant='circle' width={20} height={20} style={{ margin: 'auto' }} />
                    </TableCell>
                  </TableRow>
                ))
                : sortAnomalies(anomaliesRows).map((row, index) => {
                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={row.uuid || index}
                    >
                      <TableCell
                        align='center'
                        className={classes.anomalyCell}
                        onClick={(event) => handleClick(event, row)}
                        style={{
                          borderLeft: `5px solid ${row.subCategory?.color || basicColors.primary}`
                        }}
                      >
                        {!row.endedAt || row.endedAt === '' ? (
                          <Chip
                            size='small'
                            label='Aperta'
                            className={classes.chipOpen}
                          />
                        ) : (
                          <Chip
                            size='small'
                            label='Chiusa'
                            className={classes.chipClosed}
                          />
                        )}
                      </TableCell>
                      {showPlantColumn && (
                        <TableCell onClick={(event) => handleClick(event, row)} className={classes.anomalyCell}>
                          {renderTruncatedText(getPlantName(row, allPlants), maxPlantChars)}
                        </TableCell>
                      )}
                      <TableCell onClick={(event) => handleClick(event, row)} className={classes.anomalyCell}>
                        {renderTruncatedText(getResourceName(row, plantDevices), maxResourceChars)}
                      </TableCell>
                      <TableCell onClick={(event) => handleClick(event, row)} className={classes.anomalyCell}>
                        {renderTruncatedText(getAnomalyDescription(row), maxDescriptionChars)}
                      </TableCell>
                      <TableCell
                        onClick={(event) => handleClick(event, row)}
                        className={classes.anomalyCell}
                        align='center'
                      >
                        {row.severity?.[(row.severity?.length ?? 0) - 1]?.level || '-'}
                      </TableCell>
                      <TableCell onClick={(event) => handleClick(event, row)} className={classes.anomalyCell}>
                        {moment(row.startedAt).format('DD/MM/yyyy HH:mm')}
                        {row.endedAt ? <br /> : null}
                        {row.endedAt ? moment(row.endedAt).format('DD/MM/yyyy HH:mm') : null}
                      </TableCell>
                      <TableCell
                        onClick={(event) => handleClick(event, row)}
                        className={classes.anomalyCell}
                        align='center'
                      >
                        {returnDuration(row.duration)}
                      </TableCell>
                      <TableCell
                        onClick={(event) => handleClick(event, row)}
                        className={classes.anomalyCell}
                        align='center'
                      >
                        <Tooltip
                          title={row.externalUrl && row.externalUrl !== '' ? 'Link esterno' : 'Nessun link presente'}
                          aria-label={row.externalUrl && row.externalUrl !== '' ? 'Link esterno' : 'Nessun link presente'}
                        >
                          <SvgIcon fontSize='small'>
                            <LinkIcon color={row.externalUrl && row.externalUrl !== '' ? basicColors.primary : basicColors.blueGrayMedium} />
                          </SvgIcon>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )
                })}
            </TableBody>
          </Table>
          {isGettingPagination && (
            <Grid container justifyContent='center' alignItems='center' style={{ width: '100%', marginTop: 20 }}>
              <Grid style={{ textAlign: 'center' }}>
                <CircularProgress className={classes.cicle} variant='indeterminate' size={40} thickness={3} />
              </Grid>
            </Grid>
          )}
          {endPaginationReached && (
            <Grid container justifyContent='center' alignItems='center' style={{ width: '100%', marginTop: 16, marginBottom: 20 }}>
              <Grid style={{ textAlign: 'center' }}>
                <Typography variant='body1' align='center'>
                  Non ci sono altre anomalie!
                </Typography>
              </Grid>
            </Grid>
          )}
        </PerfectScrollbar>
      </TableContainer>
      {!isLoading && anomaliesRows[0] !== '-' && anomaliesRows.length === 0 && (
        <Grid container justifyContent='center' alignItems='center' style={{ width: '100%', marginTop: 40 }}>
          <Grid style={{ textAlign: 'center' }}>
            <img
              alt='Nessuna anomalia'
              src='/static/images/undraw_empty_xct9.svg'
              style={{
                display: 'inline-block',
                maxWidth: '100%',
                width: 200
              }}
            />
            <Typography variant='body1' align='center' style={{ marginTop: 16 }}>
              Non ci sono anomalie per questo periodo!
            </Typography>
          </Grid>
        </Grid>
      )}
    </Card>
  )
}

AnomaliesListView.propTypes = {
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  anomaliesList: PropTypes.array,
  getMoreAnomalies: PropTypes.func,
  totalAnomalies: PropTypes.number,
  limit: PropTypes.number,
  onRowClick: PropTypes.func,
  onSelectedChanged: PropTypes.func,
  onFilterChange: PropTypes.func,
  periodHasChanged: PropTypes.bool,
  devices: PropTypes.array,
  dataloggers: PropTypes.array,
  orientations: PropTypes.array,
  plants: PropTypes.array,
  showPlantColumn: PropTypes.bool
}

AnomaliesListView.defaultProps = {
  isLoading: false,
  anomaliesList: [],
  showPlantColumn: false
}

export default AnomaliesListView
